import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "@/store/hooks"
import { store } from "@/store/store"
import type { AppDispatch } from "@/store/store"
import { setModalData, setState } from "@/store/globalSlice"
import {
  addBackButtonListener,
  minimizeApp,
  runBackHandlers,
} from "@/utils/backButton"

/**
 * What Android's back button does, for the whole app. Renders nothing; it only
 * has to sit inside the router so it can navigate.
 *
 * Registering a `backButton` listener at all suppresses Capacitor's own
 * default, so every case has to be answered here. In order:
 *
 *  1. Anything that registered through `useBackHandler` — modals and overlays
 *     with their own close button. Back presses that button.
 *  2. The overlays the game keeps in the store, since those don't go through
 *     the hook (see DISMISSIBLE_OVERLAYS).
 *  3. The route. A game or a game room asks before letting go of the seat; the
 *     lobby is home and sends the app to the background; everything else walks
 *     back the way it came.
 */

type GlobalState = ReturnType<typeof store.getState>["global"]

interface DismissibleOverlay {
  isOpen: (state: GlobalState) => boolean
  close: (state: GlobalState, dispatch: AppDispatch) => void
}

/**
 * Store-driven overlays a back press may dismiss, in the order they stack —
 * first match wins, so the confirmations that open on top of other modals are
 * listed above the modals they cover.
 *
 * What is *missing* here is the point of the list. The auction, buy-or-auction,
 * the jail choice, forced buy, double rent, say-no, the decision hub, role
 * picking, bankruptcy and the stock exchange are all questions the game is
 * waiting on — several of them on a timer, and the stock exchange even answers
 * the server ("dismissed") when it closes. A back press is not an answer to any
 * of them, so it falls through to the route rules and offers to leave instead.
 */
const DISMISSIBLE_OVERLAYS: DismissibleOverlay[] = [
  {
    isOpen: (s) => !!s.modalData.fastPaceNewPlayerConfirm?.visible,
    close: (_, d) =>
      d(setModalData({ fastPaceNewPlayerConfirm: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.leaveGameConfirm?.visible,
    close: (_, d) => d(setModalData({ leaveGameConfirm: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.themeSwitcher?.visible,
    close: (_, d) => d(setModalData({ themeSwitcher: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.settings?.visible,
    close: (_, d) => d(setModalData({ settings: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.help?.visible,
    close: (_, d) => d(setModalData({ help: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.helpTourActive,
    close: (_, d) => d(setState({ helpTourActive: false })),
  },
  {
    isOpen: (s) => !!s.modalData.fullStats?.visible,
    close: (_, d) => d(setModalData({ fullStats: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.playerCards?.visible,
    // playerOrder has to survive the close — the modal reads it while it fades.
    close: (s, d) =>
      d(
        setModalData({
          playerCards: { ...s.modalData.playerCards!, visible: false },
        })
      ),
  },
  {
    isOpen: (s) => !!s.modalData.trade?.visible || !!s.viewedTradeId,
    // Both halves, the same way CreateTradeModal's own close does it: the modal
    // is open when either one is set.
    close: (_, d) => {
      d(setState({ viewedTradeId: null }))
      d(setModalData({ trade: { visible: false } }))
    },
  },
  {
    isOpen: (s) => !!s.modalData.propMng?.visible,
    close: (_, d) => d(setModalData({ propMng: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.endGame?.visible,
    close: (_, d) => d(setModalData({ endGame: { visible: false } })),
  },
  {
    isOpen: (s) => !!s.modalData.gameEndWithoutStats?.visible,
    close: (_, d) => d(setModalData({ gameEndWithoutStats: { visible: false } })),
  },
  {
    // Not a modal but a board mode, and it swallows board taps while it is on —
    // so it is exactly the kind of thing back is expected to get you out of.
    isOpen: (s) => !!s.houseBuildMode,
    close: (_, d) => d(setState({ houseBuildMode: null })),
  },
]

const closeTopOverlay = (dispatch: AppDispatch) => {
  const state = store.getState().global
  const overlay = DISMISSIBLE_OVERLAYS.find((item) => item.isOpen(state))
  if (!overlay) return false
  overlay.close(state, dispatch)
  return true
}

/**
 * Screens with nothing behind them. Back goes to the home screen rather than
 * unwinding into whatever the player passed through on the way in — the lobby
 * is where the app starts, and /login is where it starts when signed out.
 *
 * /already-in-game is here because it is a dead end by design: it exists to say
 * the account is playing elsewhere, and there is nowhere for it to go back to.
 */
const ROOT_PATHS = ["/", "/l2", "/login", "/lobby", "/already-in-game"]

/** The current route, read fresh at press time so nothing here re-renders on navigation. */
const currentPath = () => {
  const hash = window.location.hash || ""
  const path = hash.startsWith("#") ? hash.slice(1) : hash
  return path.split("?")[0] || "/"
}

/**
 * Whether there is a route behind this one. React Router keeps its position in
 * `history.state.idx`, and at 0 there is nothing to pop — `navigate(-1)` would
 * leave the button looking broken.
 */
const canGoBack = () => {
  try {
    const idx = (window.history.state as { idx?: number } | null)?.idx
    return typeof idx === "number" && idx > 0
  } catch {
    return false
  }
}

const NativeBackHandler: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const onBack = () => {
    if (runBackHandlers()) return
    if (closeTopOverlay(dispatch)) return

    const path = currentPath()

    // A seat is being given up either way — mid-game it goes to a bot, in the
    // room it just empties — and back is the easiest button on the phone to
    // hit by accident, so both ask first. This ignores
    // `featureFlags.confirmBeforeLeavingGame`, which is about the deliberate
    // Leave button; a stray swipe is a different thing.
    //
    // Only with a game actually in the store, which is what both pages render
    // the prompt underneath. Without one they are showing their loader or the
    // "game not available" screen — there is no seat to give up, and raising
    // the flag would leave it set for a modal that then appears unprompted the
    // moment the game finishes loading.
    if (path.startsWith("/game/") || path.startsWith("/newgame/")) {
      if (store.getState().global.myGame) {
        dispatch(setModalData({ leaveGameConfirm: { visible: true } }))
      } else {
        navigate("/lobby")
      }
      return
    }

    if (ROOT_PATHS.includes(path)) {
      minimizeApp()
      return
    }

    // Everything else is a page reached from the lobby — stats, friends, the
    // rules, a single game's history. Popping keeps nested pages working
    // (/game-history/:id lands back on the list); the lobby is the floor under
    // anything that was opened cold, such as a deep link.
    if (canGoBack()) navigate(-1)
    else navigate("/lobby")
  }

  // Through a ref so the native listener is registered exactly once: it is the
  // only subscription the plugin has, and re-adding it on every render would
  // churn a bridge call each time.
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => addBackButtonListener(() => onBackRef.current()), [])

  return null
}

export default NativeBackHandler
